import { questionQualityParameterLabel } from "./labels"
import type {
  QuestionQualityAlternate,
  QuestionQualityCheck,
  QuestionQualityConfidence,
  QuestionQualityField,
  QuestionQualityHistory,
  QuestionQualitySettingsTarget,
} from "./types"

export function questionQualityFieldHomeLabel(
  field: QuestionQualityField,
  settingsTarget?: QuestionQualitySettingsTarget,
): string {
  switch (field) {
    case "leadIn":
      return "Question"
    case "title":
      return "Title"
    case "option":
      return "Option"
    case "options":
      return "Options"
    case "rationale":
      return "Rationale"
    case "references":
      return "References"
    case "settings":
      if (settingsTarget === "format") return "Question format"
      if (settingsTarget === "bloom") return "Bloom’s"
      if (settingsTarget === "difficulty") return "Difficulty"
      return "Properties"
  }
}

function fieldHomeKey(
  check: QuestionQualityCheck,
  mode: "tile" | "mark" = "tile",
): string {
  if (check.field === "settings") {
    return mode === "mark"
      ? `settings:${check.settingsTarget ?? "settings"}`
      : "settings"
  }
  if (check.field === "option") return `option:${check.optionId ?? ""}`
  return check.field
}

export function questionQualityFieldSiblings(
  checks: QuestionQualityCheck[],
  check: QuestionQualityCheck,
): QuestionQualityCheck[] {
  const key = fieldHomeKey(check, "mark")
  return sortQuestionQualityChecks(
    checks.filter((item) => fieldHomeKey(item, "mark") === key),
  )
}

export function questionQualityHomeDismissIds(
  checks: QuestionQualityCheck[],
  checkId: string,
): string[] {
  const target = checks.find((item) => item.id === checkId)
  if (!target) return [checkId]
  const ids = questionQualityFieldSiblings(checks, target).map((item) => item.id)
  return ids.length > 0 ? ids : [checkId]
}

function uniqueStrings(values: string[]): string[] {
  const seen = new Set<string>()
  const unique: string[] = []
  for (const value of values) {
    const key = value.trim().toLowerCase()
    if (!key || seen.has(key)) continue
    seen.add(key)
    unique.push(value)
  }
  return unique
}

function recKeys(check: QuestionQualityCheck): string[] {
  return (check.alternates ?? [])
    .filter((item) => isApplyableQualityAlternate(item))
    .map((item) => item.text.trim().toLowerCase())
    .filter(Boolean)
}

export function collapseQuestionQualityHome(
  siblings: QuestionQualityCheck[],
  openChecks: QuestionQualityCheck[] = siblings,
): QuestionQualityCheck | null {
  const ordered = sortQuestionQualityChecks(siblings)
  const primary = ordered[0]
  if (!primary) return null
  const alternates = selectHomeRecommendations(ordered, openChecks)
  if (ordered.length === 1) {
    return alternates.length > 0 ? { ...primary, alternates } : primary
  }

  const primaryRecs = new Set(recKeys(primary))
  const extras = ordered.slice(1).filter((sibling) => {
    const keys = recKeys(sibling)
    if (keys.length === 0) return sibling.title !== primary.title
    return keys.some((key) => !primaryRecs.has(key))
  })
  const mechanisms = uniqueStrings(
    extras.length === 0
      ? [primary.mechanism]
      : [primary.mechanism, ...extras.map((item) => item.mechanism)],
  )

  return {
    ...primary,
    mechanism: mechanisms.join(" "),
    alternates: alternates.length > 0 ? alternates : primary.alternates,
  }
}

export function questionQualityVisibleHomes(
  checks: QuestionQualityCheck[],
  mode: "tile" | "mark" = "mark",
): QuestionQualityCheck[] {
  const groups = new Map<string, QuestionQualityCheck[]>()
  for (const check of checks) {
    const key = fieldHomeKey(check, mode)
    const list = groups.get(key) ?? []
    list.push(check)
    groups.set(key, list)
  }
  const homes: QuestionQualityCheck[] = []
  for (const siblings of groups.values()) {
    const home = collapseQuestionQualityHome(siblings, checks)
    if (home) homes.push(home)
  }
  return sortQuestionQualityChecks(homes)
}

export function questionQualityHomeFor(
  checks: QuestionQualityCheck[],
  checkId?: string | null,
): QuestionQualityCheck | null {
  const homes = questionQualityVisibleHomes(checks)
  if (homes.length === 0) return null
  if (!checkId) return homes[0] ?? null
  const raw = checks.find((item) => item.id === checkId)
  if (raw) {
    return collapseQuestionQualityHome(
      questionQualityFieldSiblings(checks, raw),
      checks,
    )
  }
  return homes.find((home) => home.id === checkId) ?? homes[0] ?? null
}

export function questionQualityTileLocator(
  _checks: QuestionQualityCheck[],
): string | null {
  return null
}

const REVIEW_WEIGHT = 10
const CAUTION_WEIGHT = 4
const SCORE_FLOOR = 12
const SCORE_CEILING = 100

const SEVERITY_IMPACT = {
  review: 100,
  caution: 40,
} as const

const CONFIDENCE_RANK: Record<QuestionQualityConfidence, number> = {
  high: 0,
  medium: 1,
  low: 2,
}

export function clampAuthoringConfidence(value: number): number {
  return Math.max(SCORE_FLOOR, Math.min(SCORE_CEILING, value))
}

export function authoringConfidenceScore(
  checks: QuestionQualityCheck[],
  excludeIds: Iterable<string> = [],
): number {
  const excluded = new Set(excludeIds)
  let review = 0
  let caution = 0
  for (const check of checks) {
    if (excluded.has(check.id)) continue
    if (check.severity === "review") review += 1
    else caution += 1
  }
  return clampAuthoringConfidence(100 - REVIEW_WEIGHT * review - CAUTION_WEIGHT * caution)
}

export function authoringConfidenceAfter(
  checks: QuestionQualityCheck[],
  alternate?: QuestionQualityAlternate,
): number {
  return authoringConfidenceScore(checks, alternate?.clearsCheckIds ?? [])
}

export function alternateImpactScore(
  alternate: QuestionQualityAlternate,
  checks: QuestionQualityCheck[],
): number {
  const byId = new Map(checks.map((check) => [check.id, check]))
  let score = 0
  for (const id of alternate.clearsCheckIds) {
    const match = byId.get(id)
    score += match ? SEVERITY_IMPACT[match.severity] : SEVERITY_IMPACT.caution
  }
  score +=
    alternate.parameterDeltas.filter((delta) => delta.effect === "clears").length *
    5
  return score
}

export function recommendationScoresDiffer(scores: readonly number[]): boolean {
  if (scores.length < 2) return false
  return scores.some((score) => score !== scores[0])
}

export function confidenceDelta(
  after: number,
  now: number,
): "up" | "down" | "same" {
  if (after > now) return "up"
  if (after < now) return "down"
  return "same"
}

export const QUALITY_HOVER_MIN_REM = 20
export const QUALITY_HOVER_MAX_REM = 40
export const QUALITY_HOVER_MAX_WIDTH_CSS = `min(${QUALITY_HOVER_MAX_REM}rem, calc(100vw - 2rem))`

export function clampQualityHoverWidth(
  fieldPx: number,
  viewportPx: number,
  remPx = 16,
): number {
  const min = QUALITY_HOVER_MIN_REM * remPx
  const max = Math.min(
    QUALITY_HOVER_MAX_REM * remPx,
    Math.max(viewportPx - 2 * remPx, min),
  )
  return Math.min(Math.max(fieldPx, min), max)
}

/** Hover shows one applyable suggestion, or two when the wording differs. */
export const QUALITY_HOVER_REC_MAX = 2
/** One line-item card can list every distinct applyable rec on that home. */
export const QUALITY_HOME_REC_MAX = 6

export function isApplyableQualityAlternate(
  alternate: QuestionQualityAlternate,
): boolean {
  return alternate.actionKind !== "none" && alternate.actionKind !== "open-leo"
}

export function alternateTargetsHoverField(
  alternate: QuestionQualityAlternate,
  check: QuestionQualityCheck,
): boolean {
  const kind = alternate.actionKind
  if (kind === "none" || kind === "open-leo") return false
  if (check.field === "leadIn") return Boolean(alternate.patch?.leadIn)
  if (check.field === "title") return Boolean(alternate.patch?.title)
  if (check.field === "rationale") {
    return kind === "set-rationale" || Boolean(alternate.patch?.rationale)
  }
  if (check.field === "settings") {
    if (
      kind === "set-bloom" ||
      kind === "set-difficulty" ||
      kind === "change-format"
    ) {
      return true
    }
    return Boolean(
      alternate.patch?.bloom ||
        alternate.patch?.difficulty ||
        alternate.patch?.type,
    )
  }
  if (check.field === "option") {
    if (kind === "pin-option") return true
    if (alternate.patch?.optionText) return true
    return Boolean(
      alternate.patch?.optionTexts?.some((row) => row.id === check.optionId),
    )
  }
  if (check.field === "options") {
    if (kind === "turn-shuffle-off" || kind === "pin-option") return true
    return Boolean(
      (alternate.patch?.optionTexts && alternate.patch.optionTexts.length > 0) ||
        (alternate.patch?.addOptions && alternate.patch.addOptions.length > 0),
    )
  }
  return true
}

export function selectHoverRecommendations(
  alternates: QuestionQualityAlternate[],
  checks: QuestionQualityCheck[],
  max = QUALITY_HOVER_REC_MAX,
  hoverCheck?: QuestionQualityCheck,
): QuestionQualityAlternate[] {
  const ranked = sortQualityAlternates(
    alternates.filter((item) => {
      if (!isApplyableQualityAlternate(item)) return false
      if (hoverCheck && !alternateTargetsHoverField(item, hoverCheck)) return false
      return true
    }),
    checks,
  )
  const unique: QuestionQualityAlternate[] = []
  const seen = new Set<string>()
  for (const item of ranked) {
    const key = item.text.trim().toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    unique.push(item)
    if (unique.length >= max) break
  }
  return unique
}

export function selectHomeRecommendations(
  homeChecks: QuestionQualityCheck[],
  openChecks: QuestionQualityCheck[],
  max = QUALITY_HOME_REC_MAX,
): QuestionQualityAlternate[] {
  const primary = sortQuestionQualityChecks(homeChecks)[0]
  const alternates = homeChecks.flatMap((check) => check.alternates ?? [])
  const ranked = selectHoverRecommendations(
    alternates,
    openChecks,
    max,
    primary,
  )
  if (ranked.length <= QUALITY_HOVER_REC_MAX) return ranked
  const head = ranked.slice(0, QUALITY_HOVER_REC_MAX)
  const coveredLabels = new Set(head.map((item) => item.actionLabel))
  const coveredClears = new Set(head.flatMap((item) => item.clearsCheckIds))
  const extras = ranked.slice(QUALITY_HOVER_REC_MAX).filter((item) => {
    if (!coveredLabels.has(item.actionLabel)) return true
    return item.clearsCheckIds.some((id) => !coveredClears.has(id))
  })
  return [...head, ...extras].slice(0, max)
}

export function sortQualityAlternates(
  alternates: QuestionQualityAlternate[],
  checks: QuestionQualityCheck[],
): QuestionQualityAlternate[] {
  return [...alternates].sort((a, b) => {
    const diff = alternateImpactScore(b, checks) - alternateImpactScore(a, checks)
    if (diff !== 0) return diff
    return 0
  })
}

export function sortQuestionQualityChecks(
  checks: QuestionQualityCheck[],
): QuestionQualityCheck[] {
  return [...checks].sort((a, b) => {
    if (a.severity !== b.severity) {
      return a.severity === "review" ? -1 : 1
    }
    const aRank = CONFIDENCE_RANK[a.rubricConfidence ?? "low"]
    const bRank = CONFIDENCE_RANK[b.rubricConfidence ?? "low"]
    if (aRank !== bRank) return aRank - bRank
    return a.title.localeCompare(b.title)
  })
}

function joinWords(words: string[]): string {
  if (words.length === 0) return ""
  if (words.length === 1) return words[0]
  if (words.length === 2) return `${words[0]} and ${words[1]}`
  return `${words.slice(0, -1).join(", ")}, and ${words[words.length - 1]}`
}

export function alternateWhyText(alternate: QuestionQualityAlternate): string {
  const clears = alternate.parameterDeltas
    .filter((delta) => delta.effect === "clears")
    .map((delta) => questionQualityParameterLabel(delta.parameter))
  const locked = alternate.parameterDeltas
    .filter((delta) => delta.effect === "locked")
    .map((delta) => `${questionQualityParameterLabel(delta.parameter)} stays`)
  const parts: string[] = []
  if (clears.length > 0) parts.push(`Clears ${joinWords(clears)}.`)
  if (locked.length > 0) parts.push(`${joinWords(locked)}.`)
  return parts.join(" ")
}

export interface QuestionQualityCompareColumn {
  kind: "current" | "alternate"
  label: string
  wording: string
  why: string
  severityLabel: string
  parameters: Array<{
    text: string
    tone: "flagged" | "clears" | "locked"
  }>
  openChecksLabel: string
  remainingCount: number
  clearsCount: number
  confidenceScore: number
  alternate?: QuestionQualityAlternate
  primary?: boolean
}

export function questionQualityCompareColumns(
  check: QuestionQualityCheck,
  openChecks: QuestionQualityCheck[],
  openCheckCount = openChecks.length,
): QuestionQualityCompareColumn[] {
  const ranked = sortQualityAlternates(check.alternates ?? [], openChecks)
  const currentText = check.currentText ?? check.markText ?? ""
  const nowScore = authoringConfidenceScore(openChecks)
  const current: QuestionQualityCompareColumn = {
    kind: "current",
    label: "This wording",
    wording: currentText,
    why: check.mechanism,
    severityLabel: check.severity === "review" ? "Review" : "Caution",
    parameters: [
      {
        text: `Flagged ${questionQualityParameterLabel(check.parameter)}`,
        tone: "flagged",
      },
    ],
    openChecksLabel:
      openCheckCount === 0 ? "None" : `${openCheckCount} open`,
    remainingCount: openCheckCount,
    clearsCount: 0,
    confidenceScore: nowScore,
  }

  const tryLabel = (index: number) => {
    if (ranked.length <= 1) return "Try this"
    return index === 0 ? "Try this" : "Or this"
  }

  const openIds = new Set(openChecks.map((item) => item.id))
  const columns = ranked.map((alternate, index) => {
    const clearsOpen = alternate.clearsCheckIds.filter(
      (id) => openIds.has(id) || id === check.id,
    )
    const clearsCount =
      openIds.size > 0 ? clearsOpen.length : alternate.clearsCheckIds.length
    const remaining = Math.max(0, openCheckCount - clearsCount)
    const clearsThis = alternate.clearsCheckIds.includes(check.id)
    const severityWord = check.severity === "review" ? "Review" : "Caution"
    return {
      kind: "alternate" as const,
      label: tryLabel(index),
      wording: alternate.text,
      why: alternateWhyText(alternate) || check.mechanism,
      severityLabel: clearsThis ? `Clears this ${severityWord}` : "Still open",
      parameters: alternate.parameterDeltas.map((delta) => ({
        text:
          delta.effect === "clears"
            ? `Clears ${questionQualityParameterLabel(delta.parameter)}`
            : `${questionQualityParameterLabel(delta.parameter)} stays`,
        tone: delta.effect === "clears" ? ("clears" as const) : ("locked" as const),
      })),
      openChecksLabel: remaining === 0 ? "None remain" : `${remaining} remain`,
      remainingCount: remaining,
      clearsCount,
      confidenceScore: authoringConfidenceAfter(openChecks, alternate),
      alternate,
      primary: index === 0,
    }
  })

  return current.wording || columns.length > 0 ? [current, ...columns] : []
}

export function questionQualityOpenCountLabel(
  checks: QuestionQualityCheck[],
): string | null {
  const homes = questionQualityVisibleHomes(checks)
  if (homes.length === 0) return null
  return homes.length === 1
    ? "1 enhancement"
    : `${homes.length} enhancements`
}

export function questionQualityTileCopy(
  checks: QuestionQualityCheck[],
  history: QuestionQualityHistory | null,
): { title: string; subtitle: string; tone: "danger" | "warning" | "neutral" } {
  const review = checks.filter((check) => check.severity === "review").length
  const working =
    history?.sat && history.difficultyMatches && !history.belowReviewFloor
  const preferVary =
    working ||
    (history?.sat && (history.usedCount ?? 0) >= 3 && !history.belowReviewFloor)

  const historyFloor =
    history?.sat && history.belowReviewFloor
      ? "History is below the review floor"
      : null

  const openLabel = questionQualityOpenCountLabel(checks)
  if (openLabel) {
    return {
      title: openLabel,
      subtitle: historyFloor ?? "",
      tone: review > 0 ? "danger" : "warning",
    }
  }
  if (historyFloor) {
    return {
      title: "Need review",
      subtitle: historyFloor,
      tone: "danger",
    }
  }
  if (preferVary) {
    return {
      title: "Performed as tagged",
      subtitle: "Prefer variants at Distribution",
      tone: "neutral",
    }
  }
  if (checks.length === 0) {
    return {
      title: "Ready to enhance",
      subtitle: "No open checks",
      tone: "neutral",
    }
  }
  return {
    title: "Ready to enhance",
    subtitle: "No open checks",
    tone: "neutral",
  }
}
